<template>
  <div class="w-full">
    <!-- Табы -->
    <div class="tabs" :class="tabsGridClass">
      <button v-for="(tab, index) in tabs" :key="tab" @click="setActive(tab)" class="tab"
        :class="{ 'tab--active': active === tab }">
        <span class="tab-label">
          <template v-if="icons && icons[index]">
            <img :src="active === tab
              ? icons[index].active
              : icons[index].default" class="tab-icon" alt="tab icon" />
          </template>
          <template v-else>{{ tab }}</template>
          <span v-if="badges && badges[index]" class="tab-badge" />
        </span>
        <span class="tab-line" />
      </button>


    </div>

    <!-- Контент -->
    <transition :name="contentTransition" mode="out-in">
      <div :key="activeIndex" class="tab-content">
        <slot v-if="tabs.length" :active="tabs[activeIndex]" />
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';

const props = defineProps<{
  tabs: string[];
  modelValue?: string;
  icons?: { default: string; active: string }[];
  cols?: 2 | 3 | 4;
  badges?: boolean[];
  contentTransition?: string;
}>();

const contentTransition = computed(() => props.contentTransition || 'fade');

const tabsGridClass = computed(() => {
  switch (props.cols) {
    case 3:
      return 'grid-cols-3';
    case 4:
      return 'grid-cols-4';
    default:
      return 'grid-cols-2';
  }
});


const emit = defineEmits(['update:modelValue']);
const active = ref(props.modelValue || props.tabs[0]);
const activeIndex = ref(props.tabs.indexOf(active.value));

const setActive = (tab: string) => {
  active.value = tab;
  activeIndex.value = props.tabs.indexOf(tab);
  emit('update:modelValue', tab);
};
watch(active, (val) => {
  activeIndex.value = props.tabs.indexOf(val);
});
</script>

<style scoped lang="scss">
.tabs {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
  padding: 0 8px;
  user-select: none;
  gap: 24px;
  background: var(--bg-card);
  border-bottom: 1px solid #E8E8E8;

  &.grid-cols-3{
    grid-template-columns: 1fr 1fr 1fr;
}

  .tab {
    position: relative;
    padding: 6px 0;
    font-size: 20px;
    font-weight: 500;
    color: var(--text-secondary);
    transition: color 0.25s ease, font-weight 0.25s ease;
    cursor: pointer;
    background: transparent;
    border: none;
    outline: none;
    display: flex;
    justify-content: center;
    width: 100%;

    &.tab--active,
    &:hover {
      color: var(--text-primary);
      font-weight: 700;
    }
  }

  .tab-line {
    display: none;
    position: absolute;
    bottom: -2px;
    left: 0;
    height: 2px;
    background: var(--accent-primary);
    border-radius: 100px;
    transition: all 0.34s cubic-bezier(.22, .9, .28, 1), width 0.34s cubic-bezier(.22, .9, .28, 1);
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.04),
      0 2px 10px var(--accent-glow, rgba(255, 21, 105, 0.35));
    will-change: transform, width;
    width: 100%;
  }

  .tab--active .tab-line {
    display: block;
  }

  .tab-label {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .tab-badge {
    position: absolute;
    top: -3px;
    right: -10px;
    width: 8px;
    height: 8px;
    background: #ff3b30;
    border-radius: 50%;
    box-shadow: 0 0 4px rgba(255, 59, 48, 0.6);
    animation: tab-badge-pulse 1.8s infinite ease-in-out;
  }
}

@keyframes tab-badge-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.6);
    opacity: 0.6;
  }
}

.tab-content {
  margin-top: 15px;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.25s ease;
}

.tab-slide-left-enter-active,
.tab-slide-left-leave-active,
.tab-slide-right-enter-active,
.tab-slide-right-leave-active {
  transition:
    transform 360ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 300ms ease,
    filter 300ms ease;
  will-change: transform, opacity;
}

.tab-slide-left-enter-from {
  transform: translate3d(26px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.tab-slide-left-leave-to {
  transform: translate3d(-18px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.tab-slide-right-enter-from {
  transform: translate3d(-26px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.tab-slide-right-leave-to {
  transform: translate3d(18px, 0, 0);
  opacity: 0;
  filter: blur(1px);
}

.tab-slide-left-enter-to,
.tab-slide-left-leave-from,
.tab-slide-right-enter-to,
.tab-slide-right-leave-from {
  transform: translate3d(0, 0, 0);
  opacity: 1;
  filter: blur(0);
}

@media (prefers-reduced-motion: reduce) {
  .tab-slide-left-enter-active,
  .tab-slide-left-leave-active,
  .tab-slide-right-enter-active,
  .tab-slide-right-leave-active {
    transition: none !important;
  }
}
</style>
